import { useEffect, useRef, useState } from "react";
import { Modal, type InputRef } from "antd";
import { CheckOutlined, TeamOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../../hooks/useViewport";
import {
  posModalStyles as modalStyles,
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
} from "../../modalPrimitives";
import { QuantityStepper } from "../../menu/QuantityStepper";

/** Таванът на броя — същият като в BE (`CreateBillIn.guests`, `SetBillGuestsIn`). */
const MAX_GUESTS = 999;
/** Бързият избор — една цифра = един клавиш (1…8). */
const QUICK_PICKS = [1, 2, 3, 4, 5, 6, 7, 8];

const clampGuests = (value: number) =>
  Math.max(1, Math.min(MAX_GUESTS, value));

interface GuestCountModalProps {
  open: boolean;
  /** Началният брой при отваряне. */
  initialCount: number;
  /** Записът в BE още тече (при закриване) — бутонът „Потвърди" се върти. */
  saving: boolean;
  onCancel: () => void;
  onConfirm: (guests: number) => void;
}

/**
 * „Брой гости" — пита се преди „Нова маса" или преди „Закрий" според
 * настройката „Въвеждане на брой гости" (Конфигурация → Маркиране). Голямото
 * поле с − / + е степерът от „Промени" (`QuantityStepper`) и тялото е подредено
 * като там; отдолу бърз избор 1…8. При отваряне курсорът е в полето с маркирана
 * цифра — пише се направо броят. Клавиатура: цифра извън полето = бърз
 * избор, ↑ ↓ / + − = промяна, Enter = потвърди, Esc = отказ. Без потвърден
 * брой следващият модал не се отваря — „Отказ" спира целия поток.
 */
export const GuestCountModal = ({
  open,
  initialCount,
  saving,
  onCancel,
  onConfirm,
}: GuestCountModalProps) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isDesktop } = useViewport();
  const [guests, setGuests] = useState(initialCount);
  // Суровият текст в полето — празно е позволено докато се пише наново.
  const [guestsInput, setGuestsInput] = useState(String(initialCount));
  // Курсорът е в полето с маркирана цифра при всяко отваряне — първата
  // натисната цифра я заменя („2" → „5"/„10"), Enter потвърждава.
  const inputRef = useRef<InputRef>(null);
  const confirmRef = useRef<() => void>(() => {});
  const cancelRef = useRef<() => void>(() => {});

  const setGuestCount = (value: number) => {
    const clamped = clampGuests(value);
    setGuests(clamped);
    setGuestsInput(String(clamped));
  };

  // Всяко отваряне започва от подадения брой.
  useEffect(() => {
    if (!open) return;
    setGuests(initialCount);
    setGuestsInput(String(initialCount));
  }, [open, initialCount]);

  const hasValidCount = guestsInput !== "";

  confirmRef.current = () => {
    if (!hasValidCount || saving) return;
    onConfirm(guests);
  };
  cancelRef.current = () => {
    if (saving) return;
    onCancel();
  };

  const handleInputChange = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 3);
    if (!digits) {
      setGuestsInput("");
      return;
    }
    setGuestCount(Number(digits));
  };

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const inInput = e.target instanceof HTMLInputElement;
      if (e.key === "Escape") {
        // preventDefault = „Esc е консумиран" за глобалната Esc-навигация
        e.preventDefault();
        cancelRef.current();
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        confirmRef.current();
        return;
      }
      if (e.key === "ArrowUp" || e.key === "+") {
        e.preventDefault();
        setGuestCount(guests + 1);
        return;
      }
      if (e.key === "ArrowDown" || e.key === "-") {
        e.preventDefault();
        setGuestCount(guests - 1);
        return;
      }
      // В полето цифрите се пишат (може и „12"); извън него — бърз избор.
      if (!inInput && /^[1-9]$/.test(e.key)) {
        e.preventDefault();
        setGuestCount(Number(e.key));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, guests]);

  return (
    <Modal
      open={open}
      onCancel={cancelRef.current}
      // Фокусът е след анимацията на antd Modal — иначе диалогът си го взема
      // обратно (същото като в EditModifiersModal).
      afterOpenChange={(opened) => {
        if (opened) inputRef.current?.focus({ cursor: "all" });
      }}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={isMobile ? undefined : 480}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="bg-pos-card text-pos-ink">
        <ModalHeader
          icon={<TeamOutlined />}
          title={t("ordering.guestCount.title")}
          onClose={cancelRef.current}
          isNarrow={isNarrow}
        />

        {/* Тялото е като в „Промени" (EditModifiersModal): степерът сам отгоре,
            под него секцията с моно надпис — без рамка и без надпис под числото. */}
        <div
          className={`flex flex-col gap-[18px] ${isNarrow ? "p-4" : "px-6 py-5"}`}
        >
          <QuantityStepper
            quantity={guests}
            quantityInput={guestsInput}
            onInputChange={handleInputChange}
            onDecrement={() => setGuestCount(guests - 1)}
            onIncrement={() => setGuestCount(guests + 1)}
            isMobile={isMobile}
            inputRef={inputRef}
          />

          <div className="flex flex-col gap-2.5">
            <span className="font-pos-mono text-[11px] font-semibold uppercase tracking-[.1em] text-pos-dim">
              {t("ordering.guestCount.quickPick")}
            </span>
            <div className="grid grid-cols-4 gap-2.5">
              {QUICK_PICKS.map((value) => (
                <QuickPickButton
                  key={value}
                  value={value}
                  selected={hasValidCount && value === guests}
                  isNarrow={isNarrow}
                  onClick={() => setGuestCount(value)}
                />
              ))}
            </div>
            {isDesktop && (
              <span className="text-[12.5px] font-medium leading-snug text-pos-muted">
                {t("ordering.guestCount.keyboardHint")}
              </span>
            )}
          </div>
        </div>

        <ModalFooter isNarrow={isNarrow} narrowFirst>
          <GhostButton
            label={t("common.cancel")}
            onClick={cancelRef.current}
            disabled={saving}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          <PrimaryButton
            label={t("ordering.guestCount.confirm", { count: guests })}
            onClick={confirmRef.current}
            disabled={!hasValidCount}
            loading={saving}
            shortcut={isDesktop ? "⏎" : undefined}
          />
        </ModalFooter>
      </div>
    </Modal>
  );
};

/** Бутон от бързия избор — избраният е с топъл фон и отметка горе вдясно
 *  (видът на избраната карта в „Закрий" / „Настани"). */
const QuickPickButton = ({
  value,
  selected,
  isNarrow,
  onClick,
}: {
  value: number;
  selected: boolean;
  isNarrow: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    className={`relative grid cursor-pointer place-items-center rounded-[14px] border font-pos-mono text-[22px] font-bold transition-colors ${
      isNarrow ? "h-[52px]" : "h-16"
    } ${
      selected
        ? "border-pos-accent/30 bg-pos-ready-bg text-pos-accent-ink"
        : "border-pos-line2 bg-pos-card text-pos-ink hover:border-pos-accent/30"
    }`}
  >
    {value}
    {selected && (
      <span className="absolute right-1.5 top-1.5 grid h-4 w-4 place-items-center rounded-full bg-pos-accent text-white">
        <CheckOutlined className="text-[9px]" />
      </span>
    )}
  </button>
);
